import { Component, OnInit } from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {environment} from '../../../../../environments/environment';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {faCog, faCaretRight, faClose} from '@fortawesome/pro-solid-svg-icons';
import {faQuestionCircle} from '@fortawesome/pro-regular-svg-icons';
import {Meta} from "@angular/platform-browser";

type SetPasswordErrors = '' |
    'NO_TOKEN' |
    'PASSWORD_MISSING' |
    'PASSWORD_TOO_SHORT' |
    'PASSWORDS_DONT_MATCH' |
    'CONTROLANT_API_ERROR_80' |
    'CONTROLANT_API_ERROR_81' |
    'CONTROLANT_API_ERROR_82' |
    'CONTROLANT_API_ERROR_83' |
    'CONTROLANT_API_ERROR_84' |
    'CONTROLANT_API_ERROR_85' |
    'UNKNOWN_ERROR';

// Adding this type because the error HTTP request is giving us a uppercase message property
interface ErrorResponse extends Error {
    error: {
        Message: string
    }
}

function b64DecodeUnicode(str) {
    // Going backwards: from bytestream, to percent-encoding, to original string.
    // This is required due to "The unicode problem"
    // (https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/Base64_encoding_and_decoding)
    const urlEncoded = atob(str.replace(/_/g, '/').replace(/-/g, '+')).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join('');
    return decodeURIComponent(urlEncoded);
    // return atob(str.replace(/_/g, '/').replace(/-/g, '+'));
}

const defaultPatterns = {
    weak: /^.{6,}$/,
    fair: /^(?=.*[a-z\u00E0-\u00F6\u00F8-\u00FF])(?=.*[A-Z\u00C0-\u00D6\u00D8-\u00DE])(?=.*[0-9]|[!@#$&*%<>()"'+°¨~`’´=|?:;,.\[\]^\\\/{}_\- ]).{8,}$/,
    good: /^(?=.*[a-z\u00E0-\u00F6\u00F8-\u00FF])(?=.*[A-Z\u00C0-\u00D6\u00D8-\u00DE])(?=.*[0-9])(?=.*[!@#$&*%<>()"'+°¨~`’´=|?:;,.\[\]^\\\/{}_\- ]).{10,}$/,
    excellent: /^(?=.*[a-z\u00E0-\u00F6\u00F8-\u00FF])(?=.*[A-Z\u00C0-\u00D6\u00D8-\u00DE])(?=.*[0-9])(?=.*[!@#$&*%<>()"'+°¨~`’´=|?:;,.\[\]^\\\/{}_\- ]).{12,}$/
};

@Component({
  selector: 'pmui-set-password-view',
  templateUrl: './set-password-view.component.html',
  styleUrls: ['./set-password-view.component.less']
})
export class SetPasswordViewComponent implements OnInit {

    username: string;
    token: string;

    pwStrengthLevel: number = 0;
    busy: boolean = false;
    setPasswordForm: UntypedFormGroup;
    showInfo: boolean = false;
    error: SetPasswordErrors = '';
    patternRequired: RegExp;
    pwStrengthLevelRequired: number | null = null;
    hasSubmitted: boolean = false;
    faCog = faCog;
    faCaretRight = faCaretRight;
    faQuestionCircle = faQuestionCircle;
    showTerms: boolean = false;

    static calculatePasswordStrength(password: string): number {

        let result: number = 0;

        if (password.match(defaultPatterns.weak)) {
            result = 1;

            if (password.match(defaultPatterns.fair)) {
                result = 2;

                if (password.match(defaultPatterns.good)) {
                    result = 3;

                    if (password.match(defaultPatterns.excellent)) {
                        result = 4;
                    }
                }
            }
        }

        return result;
    }

    constructor(
        private formBuilder: UntypedFormBuilder,
        private httpClient: HttpClient,
        private router: Router,
        private meta: Meta,
        activatedRoute: ActivatedRoute,
    ) {

        this.username = decodeURIComponent(activatedRoute.snapshot.params.username);
        this.token = decodeURIComponent(activatedRoute.snapshot.params.token);

        try {
            this.patternRequired = new RegExp(b64DecodeUnicode(activatedRoute.snapshot.queryParams.req));
            this.pwStrengthLevelRequired =
                this.patternRequired.source === defaultPatterns.weak.source ? 1
                    : this.patternRequired.source === defaultPatterns.fair.source ? 2
                        : this.patternRequired.source === defaultPatterns.good.source ? 3
                            : this.patternRequired.source === defaultPatterns.excellent.source ? 4 : null;
        } catch (e) {
            this.patternRequired = /^.*$/;
            this.pwStrengthLevelRequired = null;
            console.warn(e);
            console.warn('Provided req param: ', activatedRoute.snapshot.params.req);
            console.log('Falling back to default pattern required (no pattern).');
        }

        console.log('Required password level from customer setting is ', this.pwStrengthLevelRequired, this.patternRequired);

        this.setPasswordForm = this.formBuilder.group({
            pw1: ['', []],
            pw2: ['', []]
        });

        this.setPasswordForm.get('pw2').setValidators([
            Validators.required,
            (control) => (control.value !== this.setPasswordForm.value.pw1) ? { noMatch: 'Passwords dont match' } : null
        ]);

        this.setPasswordForm.get('pw1').setValidators([
            Validators.required,
            Validators.minLength(6),
            Validators.pattern(this.patternRequired),
            (control) => (this.setPasswordForm.get('pw2').touched && control.value !== this.setPasswordForm.value.pw2) ?
                { noMatch: 'Passwords dont match' } : null
        ]);

        this.setPasswordForm.get('pw1').valueChanges.subscribe(
            (value) => {
                this.pwStrengthLevel = SetPasswordViewComponent.calculatePasswordStrength(value);
            }
        );

        this.setPasswordForm.valueChanges.subscribe((value) => {
            if (this.hasSubmitted && value.pw1 === value.pw2) {
               this.error = '';
                this.setPasswordForm.get('pw1').updateValueAndValidity({ onlySelf: true })
                this.setPasswordForm.get('pw2').updateValueAndValidity({ onlySelf: true })
            }
        })
    }


    setPassword() {
        this.error = '';
        this.hasSubmitted = true;
        if (typeof this.token === 'undefined' || !this.token) {
            this.error = 'NO_TOKEN';

            return;
        }

        this.setPasswordForm.updateValueAndValidity();

        if (this.setPasswordForm.invalid) {

            const pw1 = this.setPasswordForm.get('pw1');
            const pw2 = this.setPasswordForm.get('pw2');

            if (
                (pw1.errors && pw1.errors.required) ||
                (pw2.errors && pw2.errors.required)
            ) {
                this.error = 'PASSWORD_MISSING';

                return;
            } else if (pw1.errors && pw1.errors.minlength) {

                this.error = 'PASSWORD_TOO_SHORT';

                return;
            } else if (pw1.errors && pw1.errors.pattern) {

                this.error = 'CONTROLANT_API_ERROR_82';

                return;
            } else if (pw1.errors && pw1.errors.noMatch) {

                this.error = 'PASSWORDS_DONT_MATCH';

                return;
            } else if (pw2.errors && pw2.errors.noMatch) {

                this.error = 'PASSWORDS_DONT_MATCH';

                return;
            }

            return;

        }

        this.busy = true;

        this.httpClient.post<{ message: string }>(environment.apiV1BaseUrl + '/accounts/users/set-password',
            {
                username: this.username,
                token: this.token,
                newPassword: this.setPasswordForm.value.pw1
            },
            { observe: 'response' }
        ).subscribe(
            () => {
                this.router.navigate(['/set-password-success']);
            },
            (errorResponse: ErrorResponse) => {
                this.busy = false;
                const errorData = errorResponse.error.Message;
                const errorCode = (errorData || '').split(':')[0];

                switch (errorCode) {

                    case 'CONTROLANT_API_ERROR_80':
                        // Any other reason, eg. no password reset active or username doesn't exist...
                        // No more differentiation to prevent enumeration attacks.
                    case 'CONTROLANT_API_ERROR_81':
                        // Password token invalid
                    case 'CONTROLANT_API_ERROR_82':
                        // Customer password strength requirements not met
                    case 'CONTROLANT_API_ERROR_83':
                        // Password reset timed out
                    case 'CONTROLANT_API_ERROR_84':
                        // Not enough time passed since last password change
                    case 'CONTROLANT_API_ERROR_85':
                        // Password doesn't meet reuse requirements
                        this.error = errorCode;
                        break;
                    default:
                        this.error = 'UNKNOWN_ERROR';
                        break;
                }

            }
        );

        return false;
    }

    toggleInfo() {
        this.showInfo = !this.showInfo;
    }

    ngOnInit() {
        // Set meta tags to prevent indexing
        // The robots meta tag instructs search engines not to show the page in search results
        this.meta.addTag({ name: 'robots', content: 'noindex, nofollow' });
    }

    protected readonly faClose = faClose;
}
